'use client';

import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { GlowContainerSection, GlowSkeleton, PropsWithClassName, PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import {
    DataLayerClassName,
    HomeProductCarousel,
    InnerHTML,
    ProductCarouselSkeleton,
    useTargetedProductRecommendations,
} from '../../../client';

export type TargetedProductCarouselProps = PropsWithLocale &
    PropsWithClassName & {
        enablePriceExceptionLegalMentions: boolean;
        carouselId: string;
        homeSlotCarouselIds: string[];
    };

export const TargetedProductCarousel: Component<TargetedProductCarouselProps> = ({
    locale,
    className,
    enablePriceExceptionLegalMentions,
    carouselId,
    homeSlotCarouselIds,
}) => {
    const t = useTranslations('Discover.Home.carousel');

    const { isLoading, result: recommendationsResults } = useTargetedProductRecommendations(
        carouselId,
        homeSlotCarouselIds,
    );

    const recommendationCarousel = unwrapResultSuccess(recommendationsResults);
    if (isLoading) {
        return (
            <div
                role={'status'}
                aria-live={'polite'}
                aria-busy={'true'}
                className={clsx('flex flex-col gap-1.5 tablet:gap-2 desktop:px-1.5', className)}
            >
                <GlowSkeleton height={'2rem'} width={'15rem'} containerClassName={'desktop:px-4'} />
                <ProductCarouselSkeleton navigationButtonClassName={'hidden tablet:block'} />
            </div>
        );
    }

    if (!recommendationCarousel) {
        return null;
    }

    return (
        <GlowContainerSection
            className={clsx('gap-1.5 tablet:gap-2 desktop:px-1.5', className, DataLayerClassName.targetedCarousel)}
            titleContainerClassName={'px-1 desktop:px-4'}
            title={<InnerHTML rawHtml={recommendationCarousel.title} />}
            button={recommendationCarousel.cta}
            titleClassName={'line-clamp-2'}
            dataLayer={{ id: carouselId, type: recommendationCarousel.carouselType }}
        >
            <HomeProductCarousel
                products={recommendationCarousel.products}
                wishlistBtnAriaLabel={t('ariaLabel.wishlist')}
                locale={locale}
                enablePriceExceptionLegalMentions={enablePriceExceptionLegalMentions}
            />
        </GlowContainerSection>
    );
};
